import { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Icon } from '@qga/roo-ui/components';

import useBreakpoints, { isScreen } from '@shared/v2/hooks/useBreakpoints';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import DateRange from '@/components/DateRange';
import useDestination from '@/hooks/useDestination';
import { useSearchPanelControls } from '@/v2/components/pages/PackageView/contexts/SearchPanelControlsContext';
import { useSearchPanelData } from '@/v2/components/pages/PackageView/contexts/SearchPanelDataContext/SearchPanelDataContext';

const StyledIcon = styled(Icon)`
  pointer-events: none;
  position: absolute;
  right: 10px;
`;

const StyledDateRange = styled(DateRange)`
  & > *:last-child {
    margin: 2px;
  }
`;

export type CalendarInputProps = {
  isMobileVariant?: boolean;
  campaignMessage?: string;
};

const CalendarInput = ({
  isMobileVariant,
  campaignMessage,
}: CalendarInputProps) => {
  // Screen size info
  const breakpoints = useBreakpoints();
  const isDesktopScreen = isScreen(breakpoints)('md', 'lg');

  const { searchQuery } = useSearchQuery();
  const { originCode, room: roomId } = searchQuery ?? {};

  // Destination Data
  const destination = useDestination();
  const blackoutDates = originCode
    ? destination?.blackoutDates?.[originCode]
    : undefined;
  const minimumNights = destination?.searchConfig?.minimumNights;
  const destinationCode = destination?.searchConfig?.destinationCode;

  // Modal/Dropdown Controls
  const { isCalendarOpen, openCalendar, closeCalendar, calendarRoomId } =
    useSearchPanelControls();
  const isOpen = isCalendarOpen && (isMobileVariant || isDesktopScreen); // Only open at mobile screens if mobile variant

  // Calendar Data
  const { calendar, isDisabled } = useSearchPanelData();
  const { value, onChange = () => {}, error } = calendar || {};

  // Error State
  const [isDirty, setIsDirty] = useState(false);
  const showError = !!error && isDirty;

  return (
    <div>
      <StyledDateRange
        data={{
          ...value,
          originCode,
          destinationCode,
        }}
        isDisabled={isDisabled}
        onDataChange={onChange}
        minimumNights={minimumNights}
        blackoutDates={blackoutDates}
        customBorderColor={
          showError ? 'colors.alert.warningDark' : 'colors.greys.alto'
        }
        // @ts-expect-error this is a valid type, will likely be fixed when DateRange is converted to TS
        icon={<StyledIcon name="calendarSimple" />}
        isOpen={isOpen}
        onOpen={openCalendar}
        onClose={() => {
          setIsDirty(true);
          closeCalendar();
        }}
        roomId={calendarRoomId || roomId}
        placeholder="Select travel dates"
        campaignMessage={campaignMessage}
      />
      {showError && (
        <Box bg="alert.warning" py="2" px="4" mt="2">
          {error}
        </Box>
      )}
    </div>
  );
};

export default CalendarInput;
